import { useState, useEffect, useCallback } from "react";
import { MapContainer, TileLayer, Marker, Popup, Polyline, Tooltip } from "react-leaflet";
import L from "leaflet";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, Sofa, Leaf, MapPin, Route, ListOrdered } from "lucide-react";
import { toast } from "sonner";
import { getErrorMessage } from "@/lib/errors";
import { adminListSolicitudes, adminOptimizarRuta } from "@/api";
import { MAP_CENTER, MAP_ZOOM, ESTADO_COLORS } from "@/mock";

const iconFor = (tipoServicio) =>
  L.divIcon({
    className: "",
    html: `<div style="width:16px;height:16px;border-radius:50%;border:2px solid white;box-shadow:0 0 4px rgba(0,0,0,0.4);background:${
      tipoServicio === "enseres" ? "#BD3742" : "#009940"
    }"></div>`,
    iconSize: [16, 16],
    iconAnchor: [8, 8],
  });

const paradaIcon = (orden, llena) =>
  L.divIcon({
    className: "",
    html: `<div style="width:24px;height:24px;border-radius:50%;background:${llena ? "#dc2626" : "#43695B"};border:2px solid white;box-shadow:0 0 4px rgba(0,0,0,0.4);display:flex;align-items:center;justify-content:center;color:white;font-size:11px;font-weight:bold;">${orden}</div>`,
    iconSize: [24, 24],
    iconAnchor: [12, 12],
  });

function todayStr() {
  return new Date().toISOString().split("T")[0];
}

export default function AdminMapView() {
  const [fecha, setFecha] = useState(todayStr());
  const [solicitudes, setSolicitudes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modo, setModo] = useState("solicitudes");
  const [tipoRuta, setTipoRuta] = useState("todos");
  const [ruta, setRuta] = useState(null);
  const [optimizando, setOptimizando] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const list = await adminListSolicitudes({ fecha });
      setSolicitudes(list.filter((s) => s.latitude && s.longitude));
    } catch {
      setSolicitudes([]);
    } finally {
      setLoading(false);
    }
  }, [fecha]);

  useEffect(() => {
    load();
    setRuta(null);
  }, [load]);

  const handleOptimizar = async () => {
    setOptimizando(true);
    try {
      const params = { fecha };
      if (tipoRuta !== "todos") params.tipo_servicio = tipoRuta;
      const res = await adminOptimizarRuta(params);
      if (res.paradas.length === 0) {
        toast.error("No hay recogidas con ubicación para esa fecha");
      }
      setRuta(res);
      setModo("ruta");
    } catch (err) {
      toast.error(getErrorMessage(err, "No se pudo calcular la ruta"));
    } finally {
      setOptimizando(false);
    }
  };

  const polylinePositions = ruta?.paradas?.map((p) => [p.lat, p.lng]) || [];

  return (
    <div className="bg-white rounded-xl shadow-sm p-4">
      <div className="flex flex-wrap items-center justify-between gap-4 mb-4">
        <div>
          <Label className="text-xs text-gray-500 mb-1 block">Fecha de la ruta</Label>
          <Input type="date" value={fecha} onChange={(e) => setFecha(e.target.value)} className="w-44" data-testid="map-date-input" />
        </div>
        <div className="flex items-end gap-2">
          <div>
            <Label className="text-xs text-gray-500 mb-1 block">Servicio</Label>
            <Select value={tipoRuta} onValueChange={setTipoRuta}>
              <SelectTrigger className="w-36" data-testid="ruta-tipo-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="todos">Todos</SelectItem>
                <SelectItem value="enseres">Enseres</SelectItem>
                <SelectItem value="poda">Poda</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <Button onClick={handleOptimizar} disabled={optimizando} className="bg-municipal hover:bg-municipal-dark text-white" data-testid="ruta-optimizar-btn">
            {optimizando ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Route className="w-4 h-4 mr-2" />}
            Calcular ruta óptima
          </Button>
          {ruta && (
            <Button variant="outline" className="border-gray-300" onClick={() => setModo(modo === "ruta" ? "solicitudes" : "ruta")} data-testid="ruta-toggle-vista-btn">
              {modo === "ruta" ? "Ver todas las solicitudes" : "Ver ruta calculada"}
            </Button>
          )}
        </div>
        <div className="flex items-center gap-4 text-sm">
          <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-full bg-municipal inline-block" /> Enseres</span>
          <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-full bg-valoriza-green inline-block" /> Poda</span>
          <Badge variant="outline" data-testid="map-count-badge">{solicitudes.length} recogidas</Badge>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-[500px] text-gray-400">
          <Loader2 className="w-6 h-6 animate-spin mr-2" /> Cargando mapa...
        </div>
      ) : (
        <div className="grid lg:grid-cols-[1fr_280px] gap-4">
          <div className="h-[500px] rounded-lg overflow-hidden border border-gray-200" data-testid="admin-route-map">
            <MapContainer center={MAP_CENTER} zoom={MAP_ZOOM} style={{ height: "100%", width: "100%" }}>
              <TileLayer attribution="&copy; OpenStreetMap contributors" url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
              {modo === "solicitudes" &&
                solicitudes.map((s) => (
                  <Marker key={s.id} position={[s.latitude, s.longitude]} icon={iconFor(s.tipo_servicio)}>
                    <Popup>
                      <div className="text-sm">
                        <p className="font-semibold text-municipal flex items-center gap-1.5">
                          {s.tipo_servicio === "enseres" ? <Sofa className="w-3.5 h-3.5" /> : <Leaf className="w-3.5 h-3.5" />}
                          {s.ticket_code}
                        </p>
                        <p>{s.nombre_completo}</p>
                        <p className="flex items-center gap-1 text-gray-500"><MapPin className="w-3 h-3" /> {s.direccion_completa}</p>
                        <Badge className={`mt-1 ${ESTADO_COLORS[s.estado] || ""}`}>{s.estado}</Badge>
                      </div>
                    </Popup>
                  </Marker>
                ))}
              {modo === "ruta" && ruta?.paradas?.map((p) => (
                <Marker key={p.isla_nombre} position={[p.lat, p.lng]} icon={paradaIcon(p.orden, p.llena)} data-testid={`ruta-parada-${p.orden}`}>
                  <Popup>
                    <div className="text-sm">
                      <p className="font-semibold text-municipal">Parada {p.orden}: {p.isla_nombre}</p>
                      <p className="text-gray-500">{p.distancia_m} m desde la parada anterior</p>
                      {p.llena && <p className="text-red-600 font-medium">⚠ Isla marcada como llena</p>}
                      <ul className="mt-1 list-disc list-inside">
                        {p.solicitudes.map((s) => <li key={s.ticket_code}>{s.ticket_code} — {s.nombre_completo}</li>)}
                      </ul>
                    </div>
                  </Popup>
                </Marker>
              ))}
              {modo === "ruta" && polylinePositions.length > 1 && (
                <Polyline positions={polylinePositions} pathOptions={{ color: "#BD3742", weight: 3, dashArray: "6 6" }} />
              )}
            </MapContainer>
          </div>

          {modo === "ruta" && ruta && (
            <div className="border border-gray-200 rounded-lg p-3 h-[500px] overflow-y-auto" data-testid="ruta-lista-paradas">
              <p className="text-xs font-semibold text-municipal mb-2 flex items-center gap-1.5">
                <ListOrdered className="w-3.5 h-3.5" /> Orden de recogida ({ruta.paradas.length} paradas)
              </p>
              {ruta.paradas.map((p) => (
                <div key={p.isla_nombre} className="flex items-start gap-2 py-2 border-b border-gray-50 text-xs">
                  <span className={`w-5 h-5 rounded-full flex items-center justify-center text-white font-bold shrink-0 ${p.llena ? "bg-red-600" : "bg-dorado"}`}>{p.orden}</span>
                  <div>
                    <p className="font-medium text-gray-700">{p.isla_nombre}</p>
                    <p className="text-gray-400">{p.distancia_m} m · {p.solicitudes.length} solicitud(es)</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
      {!loading && modo === "solicitudes" && solicitudes.length === 0 && (
        <p className="text-center text-gray-400 text-sm mt-4">No hay recogidas programadas para esta fecha.</p>
      )}
    </div>
  );
}
